import axios from 'axios'
import store from '@/store'

const API_BASE = 'https://residencial-services.intrare.app'
const BOARD_DIRECTIVE_BASE = `${API_BASE}/board-directive`

export function extractApiErrorMessage (error) {
  const data = error && error.response && error.response.data
  if (!data) return 'Ocurrió un error al conectar con el servidor'
  if (typeof data === 'string') return data
  if (data.detail) return data.detail

  const messages = []
  const flatten = (obj, prefix) => {
    Object.keys(obj).forEach(key => {
      const val = obj[key]
      const label = prefix ? `${prefix}.${key}` : key
      if (Array.isArray(val)) {
        val.forEach(v => messages.push(`${label}: ${v}`))
      } else if (val && typeof val === 'object') {
        flatten(val, label)
      } else {
        messages.push(`${label}: ${val}`)
      }
    })
  }
  flatten(data, '')
  return messages.join(' | ') || 'Ocurrió un error al procesar la solicitud'
}

function getHeaders () {
  return {
    'Authorization': `Token ${store.getters.getToken}`,
    'Content-Type': 'application/json'
  }
}

export default {
  /**
   * Obtiene lista de integrantes de la mesa directiva
   * @returns {Promise} Lista de integrantes
   */
  async getMembers () {
    try {
      const response = await axios.get(`${BOARD_DIRECTIVE_BASE}/members/`, { headers: getHeaders() })
      // El API retorna un array directo o un objeto con results
      if (Array.isArray(response.data)) {
        return response.data
      }
      return response.data.results || []
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Obtiene detalles de un integrante específico
   * @param {string} id - ID del integrante
   * @returns {Promise} Datos del integrante
   */
  async getMemberDetail (id) {
    try {
      const response = await axios.get(`${BOARD_DIRECTIVE_BASE}/members/${id}/`, { headers: getHeaders() })
      return response.data
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Crea un nuevo integrante de mesa directiva
   * @param {Object} data - Datos del integrante (name, position, resident, email, phone, status)
   * @returns {Promise} Integrante creado
   */
  async createMember (data) {
    try {
      const response = await axios.post(`${BOARD_DIRECTIVE_BASE}/members/`, data, { headers: getHeaders() })
      return response.data
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Actualiza un integrante de mesa directiva
   * @param {string} id - ID del integrante
   * @param {Object} data - Datos a actualizar
   * @returns {Promise} Integrante actualizado
   */
  async updateMember (id, data) {
    try {
      const response = await axios.patch(`${BOARD_DIRECTIVE_BASE}/members/${id}/`, data, { headers: getHeaders() })
      return response.data
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Elimina un integrante de mesa directiva
   * @param {string} id - ID del integrante
   * @returns {Promise} Respuesta del servidor
   */
  async deleteMember (id) {
    try {
      await axios.delete(`${BOARD_DIRECTIVE_BASE}/members/${id}/`, { headers: getHeaders() })
      return true
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Obtiene el período actual activo
   * @returns {Promise} Período actual
   */
  async getCurrentPeriod () {
    try {
      const response = await axios.get(`${BOARD_DIRECTIVE_BASE}/members/current_period/`, { headers: getHeaders() })
      return response.data
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Obtiene las posiciones disponibles
   * @returns {Promise} Lista de posiciones
   */
  async getAvailablePositions () {
    try {
      const response = await axios.get(`${BOARD_DIRECTIVE_BASE}/members/positions/`, { headers: getHeaders() })
      // El API retorna un array en positions o un array directo
      if (Array.isArray(response.data)) {
        return response.data
      }
      return response.data.positions || []
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Crea un período de mesa directiva
   * @param {Object} data - Datos del período
   * @returns {Promise} Período creado
   */
  async createPeriod (data) {
    try {
      const response = await axios.post(`${BOARD_DIRECTIVE_BASE}/periods/`, data, { headers: getHeaders() })
      return response.data
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Crea una posición en la mesa directiva
   * @param {Object} data - Datos de la posición
   * @returns {Promise} Posición creada
   */
  async createPosition (data) {
    try {
      const response = await axios.post(`${BOARD_DIRECTIVE_BASE}/positions/`, data, { headers: getHeaders() })
      return response.data
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Obtiene lista de reuniones
   * @param {string} status - Filtro de estado (upcoming, finalized, cancelled)
   * @returns {Promise} Lista de reuniones
   */
  async getMeetings (status = null) {
    try {
      let url = `${BOARD_DIRECTIVE_BASE}/meetings/`
      if (status) {
        url += `?status=${status}`
      }
      const response = await axios.get(url, { headers: getHeaders() })
      // El API retorna un array directo o un objeto con results
      if (Array.isArray(response.data)) {
        return response.data
      }
      return response.data.results || []
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Obtiene detalles de una reunión
   * @param {string} id - ID de la reunión
   * @returns {Promise} Datos de la reunión
   */
  async getMeetingDetail (id) {
    try {
      const response = await axios.get(`${BOARD_DIRECTIVE_BASE}/meetings/${id}/`, { headers: getHeaders() })
      return response.data
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Crea una nueva reunión
   * @param {Object} data - Datos de la reunión
   * @returns {Promise} Reunión creada
   */
  async createMeeting (data) {
    try {
      const response = await axios.post(`${BOARD_DIRECTIVE_BASE}/meetings/`, data, { headers: getHeaders() })
      return response.data
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Actualiza una reunión
   * @param {string} id - ID de la reunión
   * @param {Object} data - Datos a actualizar
   * @returns {Promise} Reunión actualizada
   */
  async updateMeeting (id, data) {
    try {
      const response = await axios.patch(`${BOARD_DIRECTIVE_BASE}/meetings/${id}/`, data, { headers: getHeaders() })
      return response.data
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Cancela una reunión
   * @param {string} id - ID de la reunión
   * @returns {Promise} Reunión cancelada
   */
  async cancelMeeting (id) {
    try {
      const response = await axios.patch(`${BOARD_DIRECTIVE_BASE}/meetings/${id}/`,
        { status: 'cancelled' },
        { headers: getHeaders() }
      )
      return response.data
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  },

  /**
   * Confirma asistencia de un participante
   * @param {string} meetingId - ID de la reunión
   * @param {Object} data - Datos con member_id y attendance
   * @returns {Promise} Participante actualizado
   */
  async confirmAttendance (meetingId, data) {
    try {
      const response = await axios.patch(
        `${BOARD_DIRECTIVE_BASE}/meetings/${meetingId}/confirm-attendance/`,
        data,
        { headers: getHeaders() }
      )
      return response.data
    } catch (error) {
      const err = new Error(extractApiErrorMessage(error))
      err.originalError = error
      throw err
    }
  }

}
